import useSWR, { useSWRConfig } from "swr";
import {
  fetchReadingTactics,
  newReadingTacticRepository,
} from "@/repositories/readingTactic";
import Image from "next/image";
import React, { useEffect, useState, Fragment, useRef } from "react";
import { Tactic } from "@/types/tactic";
import { useForm } from "react-hook-form";
import { useToast } from "@/utils";
import { client } from "@/api/client";
import { ReadingTactic } from "@/types/readingTactic";
import { Dialog, Transition } from "@headlessui/react";
import Link from "next/link";

export const ReadingTacticAction = ({
  tactic,
  readingTactics,
}: {
  tactic: Tactic;
  readingTactics: (data: ReadingTactic[]) => void;
}) => {
  const toast = useToast();
  const { mutate } = useSWRConfig();
  const readingTacticRepository = newReadingTacticRepository(client);
  const [loading, setLoading] = useState(false);
  const { data } = useSWR(`/api/v1/reading_tactics`, (url) =>
    fetchReadingTactics(url)
  );

  const [open, setOpen] = useState(false);
  const cancelButtonRef = useRef(null);

  useEffect(() => {
    if (data?.length) {
      readingTactics(data);
    }
  }, [data, readingTactics]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async () => {
    try {
      setLoading(true);
      const saveData = {
        tactic_id: tactic.id,
      };
      await readingTacticRepository.createReadingTactic(saveData);
      toast.success("24時間閲覧頂けます。");
      await mutate(`/api/v1/reading_tactics`);
    } catch (e) {
      if (e === 406) {
        setOpen(true);
      } else {
        toast.error("エラーが発生しました。");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {tactic?.image && (
        <Image src={tactic?.image} width={265} height={140} alt={""} />
      )}
      <p className={"text-white font-bold text-xl mb-3"}>{tactic?.title}</p>
      <p className={"text-white text-sm mb-5"}>
        本日の無料枠を利用して閲覧する
      </p>
      <form onSubmit={handleSubmit(onSubmit)}>
        <button
          type="submit"
          className={
            "duration-300 relative bg-[#e9530c] text-white p-1 w-[280px] rounded hover:opacity-60"
          }
        >
          <span className={"relative w-full h-full block"}>
            <span
              className={
                "block absolute top-0 left-0 bottom-0 bg-orange-100 py-0.5 px-1 rounded text-[#e9530c]"
              }
            >
              <span
                className={
                  "block font-helvetica text-2xl font-bold leading-[1]"
                }
              >
                24
              </span>
              <span className={"block text-xs"}>時間</span>
            </span>
            <span className={`text-xl font-bold py-2 block`}>閲覧する</span>
          </span>
        </button>
      </form>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          initialFocus={cancelButtonRef}
          onClose={setOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full sm:p-6">
                  <div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title
                        as="h3"
                        className="text-lg leading-6 font-medium text-gray-900"
                      >
                        上限を超えております
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          現在の無料会員のプレミアムコンテンツの閲覧は1日1コンテンツまでとなっております。制限なしでお楽しみ頂く場合は下記より「プランを確認する」をご選択ください。
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                    <Link
                      href={"/billing"}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm"
                    >
                      プランを確認する
                    </Link>
                    <button
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                      onClick={() => setOpen(false)}
                      ref={cancelButtonRef}
                    >
                      明日まで待つ
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};
